import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import AppleIcon from '@mui/icons-material/Apple';
import { DEFAULT_CUSTOM_ICON, MOBILITY_ICON_REGISTRY } from '../config/mobility-icon-registry';
import AndroidIcon from '@mui/icons-material/Android';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMobilityConfig } from '../hooks/useMobilityConfig';

const menuItemSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  px: 2.5,
  py: 1.75,
  borderRadius: '50px',
  border: '1px solid rgba(255,255,255,0.15)',
  bgcolor: 'rgba(255,255,255,0.07)',
  cursor: 'pointer',
  transition: 'background 0.2s, border-color 0.2s',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.13)', borderColor: 'rgba(255,255,255,0.3)' },
  '&:active': { bgcolor: 'rgba(255,255,255,0.18)' },
};

function buildVoiceFeedbackAction(navigate) {
  return () => {
    const lastStopPath = sessionStorage.getItem('lastStopPath');

    if (lastStopPath) {
      const match = lastStopPath.match(/\/stop\/(\d+)/);

      if (match?.[1]) {
        navigate(`/mobilite/voice?stopId=${match[1]}`);
        return;
      }
    }

    navigate('/mobilite/voice');
  };
}

function openExternalUrl(url) {
  if (!url) return;
  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    // ignore invalid URLs
  }
}

function buildMobilityButtonRegistry({ navigate, handleContact, buttonUrls }) {
  return {
    buy_ticket: {
      icon: <ShoppingCartOutlinedIcon />,
      labelKey: 'mobilite.buyTicket',
      action: () => openExternalUrl(buttonUrls.buy_ticket),
    },
    visio_agency: {
      icon: <VideocamOutlinedIcon />,
      labelKey: 'mobilite.visioAgency',
      action: () => openExternalUrl(buttonUrls.visio_agency),
    },
    bot_iv: {
      icon: <SmartToyOutlinedIcon />,
      labelKey: 'mobilite.botIV',
      action: () => openExternalUrl(buttonUrls.bot_iv),
    },
    voice_feedback: {
      icon: <MicNoneOutlinedIcon />,
      labelKey: 'mobilite.voiceFeedback',
      action: buildVoiceFeedbackAction(navigate),
    },
    contact: {
      icon: <PhoneOutlinedIcon />,
      labelKey: 'mobilite.contact',
      action: handleContact,
    },
  };
}

function buildButtonUrlMap(buttons) {
  return buttons.reduce((acc, button) => {
    if (button.url) acc[button.key] = button.url;
    return acc;
  }, {});
}

export default function MobilitePanel({ onContact }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config, loading } = useMobilityConfig();

  const handleContact = useCallback(() => {
    if (onContact) {
      onContact();
    } else {
      navigate('/contact');
    }
  }, [onContact, navigate]);

  const buttonUrls = useMemo(() => buildButtonUrlMap(config.buttons), [config.buttons]);

  const menuItems = useMemo(() => {
    const registry = buildMobilityButtonRegistry({ navigate, handleContact, buttonUrls });
    return (config?.buttons || [])
      .map((button) => {
        const known = registry[button.key];
        if (known) return { key: button.key, ...known };

        // Custom button: look up the stored icon key, fall back to default.
        if (button.label && button.url) {
          const IconComp = (button.icon && MOBILITY_ICON_REGISTRY[button.icon]) || DEFAULT_CUSTOM_ICON;
          return {
            key: button.key,
            icon: <IconComp />,
            label: button.label,
            action: () => openExternalUrl(button.url),
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [config, navigate, handleContact, buttonUrls]);

  const showAppStores = !loading && config.show_app_stores && (config.app_store_url || config.google_play_url);

  return (
    <Stack alignItems="stretch" spacing={0} sx={{ px: 3, py: 5, maxWidth: 440, width: '100%' }}>
      <Typography
        fontWeight={900}
        fontSize={18}
        letterSpacing={2}
        color="#fff"
        textAlign="center"
        textTransform="uppercase"
        sx={{ mb: 3 }}
      >
        {t('mobilite.title')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#4fc3f7' }} />
        </Box>
      ) : (
        <>
          <Stack spacing={1.5}>
            {menuItems.map((item) => (
              <Box
                key={item.key}
                sx={menuItemSx}
                onClick={item.action || undefined}
                role={item.action ? 'button' : undefined}
                tabIndex={item.action ? 0 : undefined}
                onKeyDown={item.action ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    item.action();
                  }
                } : undefined}
              >
                <Box sx={{ color: '#4fc3f7', display: 'flex', alignItems: 'center', fontSize: 24 }}>
                  {item.icon}
                </Box>
                <Typography
                  fontWeight={700}
                  fontSize={13}
                  letterSpacing={1}
                  color="#fff"
                  sx={{ flex: 1, textTransform: 'uppercase' }}
                >
                  {item.labelKey ? t(item.labelKey) : item.label}
                </Typography>
                <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 20 }} />
              </Box>
            ))}
          </Stack>

          {showAppStores && (
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
              {config.app_store_url && (
                <Box
                  onClick={() => openExternalUrl(config.app_store_url)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, borderRadius: 2,
                    bgcolor: '#000', border: '1px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer', minWidth: 130,
                  }}
                >
                  <AppleIcon sx={{ fontSize: 26, color: '#fff' }} />
                  <Box>
                    <Typography fontSize={9} color="rgba(255,255,255,0.6)" lineHeight={1}>{t('mobilite.downloadOn')}</Typography>
                    <Typography fontSize={13} fontWeight={700} color="#fff" lineHeight={1.3}>{t('mobilite.appStore')}</Typography>
                  </Box>
                </Box>
              )}
              {config.google_play_url && (
                <Box
                  onClick={() => openExternalUrl(config.google_play_url)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, borderRadius: 2,
                    bgcolor: '#000', border: '1px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer', minWidth: 130,
                  }}
                >
                  <AndroidIcon sx={{ fontSize: 26, color: '#3ddc84' }} />
                  <Box>
                    <Typography fontSize={9} color="rgba(255,255,255,0.6)" lineHeight={1}>{t('mobilite.downloadOn')}</Typography>
                    <Typography fontSize={13} fontWeight={700} color="#fff" lineHeight={1.3}>{t('mobilite.googlePlay')}</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
